import { useEffect, useRef } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

/**
 * Limpa o cache do React Query (memória + localStorage persistido)
 * sempre que o usuário autenticado muda ou faz logout.
 *
 * Sem isso, dados de um parceiro ficam armazenados em
 * localStorage("acelera-query-cache") e podem ser exibidos
 * para o próximo usuário que logar no mesmo navegador — um
 * vazamento de dados entre contas.
 */
const CACHE_KEY = "acelera-query-cache";

const clearPersistedCache = () => {
  try {
    if (typeof window !== "undefined") {
      window.localStorage.removeItem(CACHE_KEY);
    }
  } catch {
    /* noop */
  }
};

export const AuthCacheGuard = () => {
  const queryClient = useQueryClient();
  const lastUserIdRef = useRef<string | null | undefined>(undefined);

  useEffect(() => {
    // Bootstrap: descobre o usuário atual e marca o ref
    supabase.auth.getSession().then(({ data: { session } }) => {
      const uid = session?.user?.id ?? null;
      const storedUid =
        typeof window !== "undefined"
          ? window.localStorage.getItem("acelera-last-uid")
          : null;

      // Se o cache persistido pertence a OUTRO usuário, limpa antes
      // que qualquer query devolva dados de outro parceiro.
      if (storedUid && storedUid !== (uid ?? "")) {
        queryClient.clear();
        clearPersistedCache();
      }
      lastUserIdRef.current = uid;
      if (typeof window !== "undefined") {
        window.localStorage.setItem("acelera-last-uid", uid ?? "");
      }
    });

    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, session) => {
        const newUid = session?.user?.id ?? null;
        const prevUid = lastUserIdRef.current;

        if (event === "SIGNED_OUT") {
          queryClient.clear();
          clearPersistedCache();
          lastUserIdRef.current = null;
          if (typeof window !== "undefined") {
            window.localStorage.setItem("acelera-last-uid", "");
          }
          return;
        }

        if (event === "SIGNED_IN" || event === "TOKEN_REFRESHED" || event === "USER_UPDATED") {
          if (prevUid !== undefined && prevUid !== newUid) {
            queryClient.clear();
            clearPersistedCache();
          }
          lastUserIdRef.current = newUid;
          if (typeof window !== "undefined") {
            window.localStorage.setItem("acelera-last-uid", newUid ?? "");
          }
        }
      }
    );

    return () => {
      subscription.unsubscribe();
    };
  }, [queryClient]);

  return null;
};

export default AuthCacheGuard;
